import AsyncStorage from '@react-native-async-storage/async-storage';
import {isV2ApiError} from './v2/errors';

export const MAX_LOGIN_ATTEMPTS = 5;
export const LOGIN_LOCKOUT_DURATION_MS = 60 * 1000;

const STORAGE_KEY = 'oneapp.login_lockout.v1';

export type LoginLockoutNotificationStatus =
  | 'idle'
  | 'sending'
  | 'sent'
  | 'failed';

export interface LoginLockoutState {
  remainingAttempts: number;
  lockedUntil: number | null;
  notificationStatus: LoginLockoutNotificationStatus;
}

export const DEFAULT_LOGIN_LOCKOUT_STATE: LoginLockoutState = {
  remainingAttempts: MAX_LOGIN_ATTEMPTS,
  lockedUntil: null,
  notificationStatus: 'idle',
};

export const isCountedLoginError = (error: unknown): boolean =>
  isV2ApiError(error) &&
  (error.code === 'invalid_credentials' || error.code === 'validation_error');

export function applyCountedLoginFailure(
  state: LoginLockoutState,
  now: number = Date.now(),
): LoginLockoutState {
  const remainingAttempts = Math.max(0, state.remainingAttempts - 1);
  if (remainingAttempts > 0) {
    return {
      remainingAttempts,
      lockedUntil: null,
      notificationStatus: 'idle',
    };
  }

  return {
    remainingAttempts: 0,
    lockedUntil: now + LOGIN_LOCKOUT_DURATION_MS,
    notificationStatus: 'sending',
  };
}

export function getRemainingLockoutSeconds(
  lockedUntil: number | null,
  now: number = Date.now(),
): number {
  if (!lockedUntil) {
    return 0;
  }
  return Math.max(0, Math.ceil((lockedUntil - now) / 1000));
}

export async function loadLoginLockoutState(
  now: number = Date.now(),
): Promise<LoginLockoutState> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULT_LOGIN_LOCKOUT_STATE;
    }

    const parsed = JSON.parse(raw) as Partial<LoginLockoutState>;
    const remainingAttempts = Number.isInteger(parsed.remainingAttempts)
      ? Math.min(
          MAX_LOGIN_ATTEMPTS,
          Math.max(0, parsed.remainingAttempts as number),
        )
      : MAX_LOGIN_ATTEMPTS;
    const lockedUntil =
      typeof parsed.lockedUntil === 'number' ? parsed.lockedUntil : null;

    if (lockedUntil !== null && lockedUntil <= now) {
      await clearLoginLockoutState();
      return DEFAULT_LOGIN_LOCKOUT_STATE;
    }

    const storedStatus = parsed.notificationStatus;
    const notificationStatus: LoginLockoutNotificationStatus =
      storedStatus === 'sent' || storedStatus === 'failed'
        ? storedStatus
        : lockedUntil
          ? 'failed'
          : 'idle';

    return {remainingAttempts, lockedUntil, notificationStatus};
  } catch (error) {
    console.warn('[loginLockout] failed to load state:', error);
    return DEFAULT_LOGIN_LOCKOUT_STATE;
  }
}

export async function persistLoginLockoutState(
  state: LoginLockoutState,
): Promise<void> {
  try {
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('[loginLockout] failed to persist state:', error);
  }
}

export async function clearLoginLockoutState(): Promise<void> {
  try {
    await AsyncStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[loginLockout] failed to clear state:', error);
  }
}
